import { Edit as EditIcon } from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    TextField,
    Box,
    Typography,
} from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { successToast, errorToast } from '@/common/toast';
import { MarkdownEditor } from '@/components/common';
import { useUpdateAssignment } from '@/hooks/assignments.hook';

/**
 * 과제 수정 모달 컴포넌트
 * 
 * 기존 과제 정보를 수정하는 모달입니다.
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {Object} props.assignment - 수정할 과제 데이터
 * @param {Function} props.onSuccess - 수정 성공 시 콜백
 */
const AssignmentEditModal = ({
    open,
    onClose,
    assignment,
    onSuccess,
}) => {
    // 과제 수정 mutation
    const updateAssignmentMutation = useUpdateAssignment();

    // 폼 관리
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            title: '',
            description: '',
            attachedFileUrl: '',
        },
    });

    // 과제 데이터가 변경되면 폼 초기화
    useEffect(() => {
        if (assignment && open) {
            reset({
                title: assignment.title || '',
                description: assignment.description || '',
                attachedFileUrl: assignment.attachedFileUrl || '',
            });
        }
    }, [assignment, open, reset]);

    /**
     * 모달 닫기 핸들러
     */
    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    /**
     * 폼 제출 핸들러
     */
    const onSubmit = useCallback(async (data) => {
        if (!assignment) {
            errorToast('과제 정보를 불러올 수 없습니다.');
            return;
        }

        try {
            await updateAssignmentMutation.mutateAsync({
                assignmentId: assignment.assignmentId,
                courseId: assignment.courseVideo?.courseId,
                videoId: assignment.courseVideo?.videoId,
                title: data.title,
                description: data.description,
                attachedFileUrl: data.attachedFileUrl || null,
            });
            successToast('과제가 수정되었습니다.');
            handleClose();
            onSuccess?.();
        } catch (error) {
            errorToast(`과제 수정에 실패했습니다. - ${error.response?.data?.msg}`);
        }
    }, [assignment, updateAssignmentMutation, handleClose, onSuccess]);

    if (!assignment) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
        >
            <DialogTitle>
                과제 수정
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* 동영상 정보 (읽기 전용) */}
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            해당 강의
                        </Typography>
                        <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                                {assignment.courseVideo?.videoName || '-'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* 과제 정보 입력 섹션 */}
                    <Box>
                        <Stack spacing={2}>
                            {/* 과제명 */}
                            <Controller
                                control={control}
                                name="title"
                                rules={{
                                    required: '과제명을 입력해주세요.',
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="과제명 *"
                                        fullWidth
                                        variant="outlined"
                                        error={!!errors.title}
                                        helperText={errors.title?.message}
                                    />
                                )}
                            />

                            {/* 설명 */}
                            <Controller
                                control={control}
                                name="description"
                                rules={{
                                    required: '설명을 입력해주세요.',
                                }}
                                render={({ field }) => (
                                    <MarkdownEditor
                                        value={field.value}
                                        onChange={field.onChange}
                                        label="설명 *"
                                        error={!!errors.description}
                                        helperText={errors.description?.message}
                                        height={400}
                                    />
                                )}
                            />

                            {/* 참고 링크 */}
                            <Controller
                                control={control}
                                name="attachedFileUrl"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="참고 링크"
                                        fullWidth
                                        variant="outlined"
                                        placeholder="https://example.com"
                                        helperText="참고할 링크를 입력해주세요. (선택사항)"
                                    />
                                )}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>취소</Button>
                <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleSubmit(onSubmit)}
                    disabled={updateAssignmentMutation.isPending}
                >
                    수정
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignmentEditModal;
